import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { MagnifyingGlass, Export, CaretLeft, CaretRight } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { API } from "@/config/constants";
import Stats from "@/components/Stats";
import EntryCard from "@/components/EntryCard";
import EmptyState from "@/components/EmptyState";
import { useAuth } from "@/context/AuthContext";

const ITEMS_PER_PAGE = 9;

const HomePage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [entries, setEntries] = useState([]);
  const [stats, setStats] = useState({ total_entries: 0, total_species_identified: 0, total_photos: 0 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selectedTag, setSelectedTag] = useState("all");
  const [tags, setTags] = useState([]);
  const [page, setPage] = useState(1);

  const fetchData = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (search) params.append("search", search);
      if (selectedTag && selectedTag !== "all") params.append("tag", selectedTag);

      const [entriesRes, statsRes, tagsRes] = await Promise.all([
        axios.get(`${API}/entries?${params}`),
        axios.get(`${API}/stats`),
        axios.get(`${API}/tags`)
      ]);

      setEntries(entriesRes.data);
      setStats(statsRes.data);
      setTags(tagsRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load entries");
    } finally {
      setLoading(false);
    }
  }, [search, selectedTag]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    setPage(1);
  }, [search, selectedTag]);


  const handleExport = async (format) => {
    try {
      const response = await axios.get(`${API}/export?format=${format}`, {
        responseType: format === "csv" ? "blob" : "json"
      });

      if (format === "csv") {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "nature_journal_export.csv");
        document.body.appendChild(link);
        link.click();
        link.remove();
      } else {
        const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: "application/json" });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "nature_journal_export.json");
        document.body.appendChild(link);
        link.click();
        link.remove();
      }
      toast.success(`Exported as ${format.toUpperCase()}`);
    } catch (error) {
      toast.error("Export failed");
    }
  };

  return (
    <div className="max-w-7xl mx-auto page-content" data-testid="home-page">
      <Stats stats={stats} />

      {/* Search and Filters */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="relative flex-1">
          <MagnifyingGlass size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#7C8068]" />
          <Input
            placeholder="Search entries..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10 bg-[#EAE7DE] border-[#D2C2B3] focus:border-[#55624F]"
            data-testid="search-input"
          />
        </div>
        <Select value={selectedTag} onValueChange={setSelectedTag}>
          <SelectTrigger className="w-full sm:w-48 bg-[#EAE7DE] border-[#D2C2B3]" data-testid="tag-filter">
            <SelectValue placeholder="Filter by tag" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Tags</SelectItem>
            {tags.map(tag => (
              <SelectItem key={tag.name} value={tag.name}>
                {tag.name} ({tag.count})
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {user && (
          <Dialog>
            <DialogTrigger asChild>
              <Button variant="outline" className="bg-[#55624F] border-[#55624F] text-white hover:bg-[#424D3D]" data-testid="export-btn">
                <Export size={20} className="mr-2" />
                Export
              </Button>
            </DialogTrigger>
          <DialogContent className="bg-white border-[#D2C2B3]">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl">Export Journal</DialogTitle>
            </DialogHeader>
            <div className="flex gap-4 mt-4">
              <Button onClick={() => handleExport("json")} className="flex-1 bg-[#55624F] hover:bg-[#424D3D]" data-testid="export-json">
                Export JSON
              </Button>
              <Button onClick={() => handleExport("csv")} className="flex-1 bg-[#55624F] hover:bg-[#424D3D]" data-testid="export-csv">
                Export CSV
              </Button>
            </div>
          </DialogContent>
        </Dialog>
        )}
      </div>

      {/* Entries Grid */}
      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          {[1, 2, 3, 4, 5, 6].map(i => (
            <Card key={i} className="border-[#D2C2B3]">
              <Skeleton className="aspect-video bg-[#EAE7DE]" />
              <CardContent className="p-4">
                <Skeleton className="h-6 w-3/4 mb-2 bg-[#EAE7DE]" />
                <Skeleton className="h-4 w-full mb-1 bg-[#EAE7DE]" />
                <Skeleton className="h-4 w-2/3 bg-[#EAE7DE]" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : entries.length === 0 ? (
        <EmptyState
          message="Your nature journal is empty. Start documenting your adventures!"
          actionLabel="Create First Entry"
          onAction={() => navigate("/entry/new")}
        />
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {entries.slice((page - 1) * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE).map(entry => (
              <EntryCard
                key={entry.id}
                entry={entry}
                onClick={() => navigate(`/entry/${entry.id}`)}
              />
            ))}
          </div>
          {entries.length > ITEMS_PER_PAGE && (
            <div className="flex items-center justify-center gap-3 mt-6">
              <Button
                variant="outline"
                onClick={() => { setPage(p => Math.max(1, p - 1)); window.scrollTo(0, 0); }}
                disabled={page === 1}
                className="bg-[#55624F] border-[#55624F] text-white hover:bg-[#424D3D] disabled:opacity-30"
                data-testid="prev-page-btn"
              >
                <CaretLeft size={18} className="mr-1" />Previous
              </Button>
              <span className="text-white text-sm font-['Manrope']">
                {page} / {Math.ceil(entries.length / ITEMS_PER_PAGE)}
              </span>
              <Button
                variant="outline"
                onClick={() => { setPage(p => Math.min(Math.ceil(entries.length / ITEMS_PER_PAGE), p + 1)); window.scrollTo(0, 0); }}
                disabled={page >= Math.ceil(entries.length / ITEMS_PER_PAGE)}
                className="bg-[#55624F] border-[#55624F] text-white hover:bg-[#424D3D] disabled:opacity-30"
                data-testid="next-page-btn"
              >
                Next<CaretRight size={18} className="ml-1" />
              </Button>
            </div>
          )}
        </>
      )}

      <div className="text-center mt-12 mb-8">
        <a
          href="https://conniekelts.com/"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 text-sm text-white hover:text-white/80 transition-colors font-['Manrope']"
          data-testid="return-to-site-link"
        >
          <img src={require("../assets/ck-design-avatar.png")} alt="" className="w-8 h-8 rounded-full object-cover border border-white/30 bg-white" />
          &larr; Return to CK Design
        </a>
      </div>
    </div>
  );
};

export default HomePage;
